/**
 * Org Enrollment API wrapper.
 *
 *   POST /api/v1/org/enroll                       — start a new enrollment (idempotent)
 *   GET  /api/v1/org/enrollment/{id}              — fetch status
 *   POST /api/v1/org/enrollment/{id}/retry        — retry a failed/partial/stale enrollment
 *
 * The endpoints encode meaningful state in the HTTP status code (200 / 202 /
 * 207 / 400 / 409 / 429 etc.), so this wrapper resolves on every non-2xx
 * response and returns the raw axios envelope. The page hook branches on
 * `result.status` instead of try/catching.
 */

import { AxiosError, AxiosResponse } from "axios"
import { api } from "../common/utils"
import {
  ORG_ENROLL_ENDPOINT,
  ORG_ENROLLMENT_ENDPOINT,
  ORG_ENROLLMENT_RETRY_ENDPOINT,
  ORG_ENROLLMENT_LOOKUP_ENDPOINT,
  ORG_ENROLLMENT_DELETE_ENDPOINT
} from "../common/constants/api-endpoints"
import {
  EnrollmentResponse,
  EnrollRequest,
  GenericError,
  ValidationError
} from "../pages/enroll/types"

export type EnrollApiBody = EnrollmentResponse | ValidationError | GenericError

export interface EnrollApiResult {
  /** HTTP status code — always populated, even for network errors (0) */
  status: number
  body: EnrollApiBody | null
  /** Set when the request never reached the server (DNS, offline, etc.) */
  networkError?: string
}

const toResult = (
  response: AxiosResponse<EnrollApiBody> | undefined,
  err?: AxiosError
): EnrollApiResult => {
  if (response) {
    return { status: response.status, body: response.data ?? null }
  }
  return {
    status: 0,
    body: null,
    networkError: err?.message ?? "Network request failed"
  }
}

const swallow = async (
  promise: Promise<AxiosResponse<EnrollApiBody>>
): Promise<EnrollApiResult> => {
  try {
    const response = await promise
    return toResult(response)
  } catch (err) {
    const axiosErr = err as AxiosError<EnrollApiBody>
    return toResult(axiosErr.response, axiosErr)
  }
}

const enroll = {
  start: (payload: EnrollRequest): Promise<EnrollApiResult> =>
    swallow(
      api.post<EnrollApiBody>(ORG_ENROLL_ENDPOINT, payload, {
        // 4xx/207/202 are normal responses for this endpoint, not exceptions
        validateStatus: () => true
      })
    ),

  get: (id: number): Promise<EnrollApiResult> =>
    swallow(
      api.get<EnrollApiBody>(ORG_ENROLLMENT_ENDPOINT(id), {
        validateStatus: () => true
      })
    ),

  retry: (id: number): Promise<EnrollApiResult> =>
    swallow(
      api.post<EnrollApiBody>(ORG_ENROLLMENT_RETRY_ENDPOINT(id), undefined, {
        validateStatus: () => true
      })
    ),

  lookupByKey: (key: string): Promise<EnrollApiResult> =>
    swallow(
      api.get<EnrollApiBody>(ORG_ENROLLMENT_LOOKUP_ENDPOINT, {
        params: { key },
        validateStatus: () => true
      })
    ),

  delete: (id: number): Promise<EnrollApiResult> =>
    swallow(
      api.delete<EnrollApiBody>(ORG_ENROLLMENT_DELETE_ENDPOINT(id), {
        validateStatus: () => true
      })
    )
}

export default enroll
